/**
 * Password Reset Page
 * Handles password reset flow
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './PasswordResetPage.css';

const PasswordResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState<'request' | 'verify' | 'reset' | 'success'>('request');
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState(token || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (token) {
      setStep('reset');
      setResetToken(token);
      verifyToken(token);
    }
  }, [token]);

  const verifyToken = async (tokenToVerify: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiService.verifyPasswordResetToken(tokenToVerify);
      if (!result.valid) {
        setError('Invalid or expired reset token');
        setStep('request');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify token');
      setStep('request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.requestPasswordReset(email);
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!resetToken) {
      setError('Please enter the reset token');
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiService.verifyPasswordResetToken(resetToken);
      if (result.valid) {
        setStep('reset');
      } else {
        setError('Invalid or expired reset token');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify token');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.resetPassword(resetToken, newPassword);
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="password-reset-page">
      <div className="reset-container">
        <div className="reset-card">
          <div className="reset-logo">
            <div className="reset-logo-icon">VS</div>
            <div className="reset-logo-text">VERSUS</div>
            <div className="reset-logo-sub">Password Reset</div>
          </div>

          {error && (
            <div className="reset-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Request Reset */}
          {step === 'request' && (
            <div className="reset-step">
              <h2>Reset Your Password</h2>
              <p>Enter your email address and we'll send you a reset link</p>
              <form onSubmit={handleRequestReset}>
                <div className="reset-form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@versus.bet"
                    disabled={isLoading}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </button>
            </div>
          )}

          {/* Step 2: Verify Token */}
          {step === 'verify' && (
            <div className="reset-step">
              <h2>Check Your Email</h2>
              <p>We've sent a reset token to {email}. Enter it below:</p>
              <form onSubmit={handleVerifyToken}>
                <div className="reset-form-group">
                  <label>Reset Token</label>
                  <input
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Enter token from email"
                    disabled={isLoading}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify Token'}
                </button>
              </form>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setStep('request')}
              >
                Back
              </button>
            </div>
          )}

          {/* Step 3: Reset Password */}
          {step === 'reset' && (
            <div className="reset-step">
              <h2>Set New Password</h2>
              <p>Enter your new password</p>
              <form onSubmit={handleResetPassword}>
                <div className="reset-form-group">
                  <label>New Password (min 8 characters)</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="reset-form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="reset-step">
              <div className="reset-success">
                <span>✅</span>
                <h2>Password Reset Successful</h2>
                <p>Your password has been reset. You can now log in with your new password.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
